import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Discount } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteDiscount } from '../hooks/use-discount-mutations';

export interface DeleteDiscountDialogProps {
  storeId: string;
  discount: Discount | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/** Confirms deleting a discount and its codes. Placed orders keep their applied amounts. */
export function DeleteDiscountDialog({
  storeId,
  discount,
  onOpenChange,
  onDeleted,
}: DeleteDiscountDialogProps) {
  const { t } = useTranslation(['discounts', 'common', 'errors']);
  const remove = useDeleteDiscount(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={discount !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('discounts:delete.title', { title: discount?.title ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('discounts:delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (discount) {
                remove.mutate(discount.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                });
              }
            }}
          >
            {remove.isPending ? t('discounts:delete.submitting') : t('discounts:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
